/**
 * VideoCarousel - Interactive carousel component for video content
 *
 * @class
 * @description Creates a carousel for video content with touch/mouse drag support
 * and navigation buttons.
 *
 * @param {HTMLElement} container - The DOM element containing slide elements
 *
 * @example
 * // HTML structure
 * // <div class="carousel-container">
 * //   <div data-video-carousel>
 * //     <div>Slide 1</div>
 * //     <div>Slide 2</div>
 * //     <div>Slide 3</div>
 * //   </div>
 * //   <button data-video-prev>Previous</button>
 * //   <button data-video-next>Next</button>
 * // </div>
 *
 * // JavaScript initialization
 * const carouselElement = document.querySelector('[data-video-carousel]');
 * new VideoCarousel(carouselElement);
 */
class VideoCarousel {
    constructor(container) {
      if (!container) return;
      this.container = container;
      this.slides = Array.from(container.children);
      if (!this.slides.length) return;

      const parent = container.parentElement;
      this.leftBtn = parent.querySelector('[data-video-prev]');
      this.rightBtn = parent.querySelector('[data-video-next]');

      this.currentIndex = 0;
      this.startX = undefined;
      this.currentX = undefined;
      this.isDragging = false;
      this.clickableTarget = null;

      this.baseClasses = ['tw-transition-all', 'tw-duration-400', 'tw-absolute', 'tw-ease-in-out'];
      this.hiddenState = ['tw-hidden'];
      this.centerState = ['tw-top-0', 'tw-transform', 'tw-w-5/6', 'tw-relative', 'tw-z-10', 'tw-left-0', 'tw-right-0', 'tw-opacity-100'];
      this.leftState = ['tw--translate-x-7', 'lg:tw--translate-x-10', 'tw--translate-y-4', 'lg:tw--translate-y-6', 'tw-scale-75', 'tw-opacity-40', 'tw-z-9'];
      this.rightState = ['tw-translate-x-7', 'lg:tw-translate-x-10', 'tw--translate-y-4', 'lg:tw--translate-y-6', 'tw-scale-75', 'tw-opacity-40', 'tw-z-8'];

      this.init();
    }

    init() {
      this.slides.forEach(slide => {
        this.baseClasses.forEach(cls => slide.classList.add(cls));
      });
      this.container.setAttribute('tabindex', '0');
      this.container.setAttribute('role', 'region');
      this.container.setAttribute('aria-label', 'Video Carousel');
      this.attachEvents();
      this.render();
    }

    attachEvents() {
      if (this.leftBtn) this.leftBtn.addEventListener('click', () => this.showPrev());
      if (this.rightBtn) this.rightBtn.addEventListener('click', () => this.showNext());
      this.container.addEventListener('keydown', (e) => this.onKeyDown(e));
      this.container.addEventListener('touchstart', (e) => this.onDragStart(e), { passive: false });
      this.container.addEventListener('touchmove', (e) => this.onDragMove(e), { passive: false });
      this.container.addEventListener('touchend', () => this.onDragEnd());
      this.container.addEventListener('mousedown', (e) => this.onDragStart(e));
      this.container.addEventListener('mousemove', (e) => this.onDragMove(e));
      this.container.addEventListener('mouseup', () => this.onDragEnd());
      this.container.addEventListener('mouseleave', () => this.onDragEnd());
    }

    onKeyDown(e) {
      if (e.key === 'ArrowLeft') return this.showPrev();
      if (e.key === 'ArrowRight') return this.showNext();
    }

    onDragStart(e) {
      this.clickableTarget = e.target.closest('a, button, [role="button"]');
      this.startX = (e.touches ? e.touches[0] : e).clientX;
      this.currentX = this.startX;
      this.isDragging = false;
    }

    onDragMove(e) {
      if (this.startX === undefined) return;
      this.currentX = (e.touches ? e.touches[0] : e).clientX;
      const delta = this.currentX - this.startX;

      if (!this.isDragging) {
        if (Math.abs(delta) < 10) return;
        this.isDragging = true;
        e.preventDefault();
      }

      if (Math.abs(delta) > 50) {
        delta > 0 ? this.showPrev() : this.showNext();
        this.onDragEnd();
      }
    }

    onDragEnd() {
      this.isDragging = false;
      this.startX = undefined;
      this.currentX = undefined;
      this.clickableTarget = null;
    }

    showPrev() {
      if (this.currentIndex <= 0) return;
      this.currentIndex--;
      this.render();
    }

    showNext() {
      if (this.currentIndex >= this.slides.length - 1) return;
      this.currentIndex++;
      this.render();
    }

    updateSlideState(slide, newStateClasses, isActive) {
      slide.classList.remove(...this.hiddenState, ...this.centerState, ...this.leftState, ...this.rightState);
      slide.classList.add(...newStateClasses);
      slide.style.pointerEvents = isActive ? 'auto' : 'none';
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    }

    toggleText(slide, show) {
      const textElem = slide.querySelector('[data-card-text]');
      if (textElem) textElem.classList.toggle('tw-hidden', !show);
    }

    render() {
      const getSlideState = (index) => {
        const positionMap = {
          [this.currentIndex]: { state: this.centerState, active: true, showText: true },
          [this.currentIndex - 1]: { state: this.leftState, active: false, showText: false },
          [this.currentIndex + 1]: { state: this.rightState, active: false, showText: false }
        };

        return positionMap[index] || { state: this.hiddenState, active: false, showText: false };
      };

      this.slides.forEach((slide, index) => {
        const { state, active, showText } = getSlideState(index);
        this.updateSlideState(slide, state, active);
        this.toggleText(slide, showText);
      });

      const isAtStart = this.currentIndex === 0;
      const isAtEnd = this.currentIndex === this.slides.length - 1;

      if (this.leftBtn) {
        this.leftBtn.disabled = isAtStart;
        this.leftBtn.classList.toggle('tw-opacity-50', isAtStart);
      }

      if (this.rightBtn) {
        this.rightBtn.disabled = isAtEnd;
        this.rightBtn.classList.toggle('tw-opacity-50', isAtEnd);
      }
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-video-carousel]').forEach(container => {
      new VideoCarousel(container);
    });
  });